import { useEffect, useRef, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";
import { z } from "zod";

import { useLiveReload } from "@thalamos/frontend";
import {
  WAITING_WORK_GROUPS,
  WAITING_WORK_PAGE_LIMIT,
  WaitingWorkGroupSchema,
  type AssessmentId,
  type OrganisationId,
  type WaitingWorkGroup,
  type WaitingWorkSlice,
} from "@thalamos/haven-common";

import { readAssessmentProgress } from "../../api/formDraft/readAssessmentProgress.js";
import type { ProgressReading } from "../../api/formDraft/readAssessmentProgress.js";
import api from "../../api/index.js";
import { waitingWorkChannels } from "../../api/waitingWork/api.js";
import {
  useSwitchSessionContext,
  type SessionContextSwitchProblem,
} from "../../Context/useSwitchSessionContext.js";
import {
  useWaitingWork,
  waitingShownBy,
  type WaitingWorkListing,
} from "../../Context/WaitingWorkContext.js";
import { createRoute } from "../../routing/createRoute.js";
import { WaitingWorkGroupSection } from "./WaitingWorkGroupSection.js";

/** How many rows each open group shows. */
type OpenGroups = Partial<Record<WaitingWorkGroup, number>>;

/** What each open group has loaded. */
type Expanded = Partial<Record<WaitingWorkGroup, WaitingWorkSlice>>;

/**
 * Kept on the history entry, so coming back to the page from acting on a row
 * reopens each group as it was. A fresh visit carries none, so every group
 * opens collapsed.
 */
const OpenGroupsSchema = z.object({
  openGroups: z.partialRecord(WaitingWorkGroupSchema, z.number().int()),
});

/** Everything waiting on the signed-in user where they act. */
export const YourActionsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const switchSessionContext = useSwitchSessionContext();
  const { listing, list } = useWaitingWork();
  const waiting = waitingShownBy(listing);
  const [expanded, setExpanded] = useState<Expanded>({});
  const [switchProblem, setSwitchProblem] =
    useState<SessionContextSwitchProblem | null>(null);
  const [progress, setProgress] = useState<
    ReadonlyMap<AssessmentId, ProgressReading>
  >(new Map());

  // Rows loaded into an open group are watched too, so acting on one
  // elsewhere lists again and the effect below reads the open groups again.
  useLiveReload(
    waitingWorkChannels([
      ...(waiting?.groups ?? []),
      ...Object.values(expanded),
    ]),
    () => {
      void list();
    },
  );

  // The open groups as last asked for, then as loaded once that read lands, so
  // a click made while a read is out builds on it rather than on what is still
  // shown.
  const askedFor = useRef<OpenGroups>({});
  const readsStarted = useRef(0);

  // Each open group is read from its first row every time, so what is shown
  // is one consistent list however much changed between reads. Only the read
  // started last, and not since abandoned, is shown, whatever order the reads
  // return in.
  const show = async (openGroups: OpenGroups) => {
    askedFor.current = openGroups;
    readsStarted.current += 1;
    const readNumber = readsStarted.current;
    const read = await Promise.all(
      WAITING_WORK_GROUPS.flatMap((group) => {
        const limit = openGroups[group];

        // A group that cannot be read, for whatever reason, closes, as one
        // refused does.
        return limit === undefined
          ? []
          : [api.waitingWork.rows({ group, limit }).catch(() => undefined)];
      }),
    );

    if (readNumber !== readsStarted.current) {
      return;
    }

    const loaded: Expanded = Object.fromEntries(
      read.flatMap((slice) =>
        slice === undefined ? [] : [[slice.group, slice]],
      ),
    );
    askedFor.current = openGroupsOf(loaded);
    setExpanded(loaded);
    navigate(".", { replace: true, state: { openGroups } });
  };

  const openGroupsOf = (current: Expanded): OpenGroups =>
    Object.fromEntries(
      Object.values(current).map((slice) => [slice.group, slice.rows.length]),
    );

  const closeEveryGroup = () => {
    askedFor.current = {};
    readsStarted.current += 1;
    setExpanded({});
    navigate(".", { replace: true, state: { openGroups: {} } });
  };

  // Reopens the groups that were open when the user left to act on a row.
  useEffect(() => {
    const remembered = OpenGroupsSchema.safeParse(location.state);

    if (remembered.success) {
      void show(remembered.data.openGroups);
    }
    // Once, for the history entry the page opened on.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rows loaded into an open group are read again whenever what is waiting
  // changes after it was first shown.
  const shownWaiting = useRef(waiting);
  useEffect(() => {
    const before = shownWaiting.current;
    shownWaiting.current = waiting;

    if (
      before !== undefined &&
      before !== waiting &&
      Object.keys(askedFor.current).length > 0
    ) {
      void show(askedFor.current);
    }
    // Only a change in what is waiting asks for the open groups again.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting]);

  // Reads how far each visible in-progress assessment has got, once each.
  useEffect(() => {
    const visible = [
      ...(waiting?.groups ?? []),
      ...Object.values(expanded),
    ].flatMap((slice) =>
      slice.group === "assessments_in_progress"
        ? slice.rows.map((row) => row.assessmentId)
        : [],
    );
    const unread = [...new Set(visible)].filter((id) => !progress.has(id));

    if (unread.length === 0) {
      return;
    }

    void Promise.all(
      unread.map(
        async (assessmentId) =>
          [assessmentId, await readAssessmentProgress(assessmentId)] as const,
      ),
    ).then((readings) => {
      setProgress((current) => new Map([...current, ...readings]));
    });
  }, [waiting, expanded, progress]);

  return (
    <YourActionsPageContent
      listing={listing}
      expanded={expanded}
      progressOf={(assessmentId) => progress.get(assessmentId)}
      now={new Date()}
      onShowAll={(group) =>
        show({ ...askedFor.current, [group]: WAITING_WORK_PAGE_LIMIT })
      }
      onShowMore={(group) =>
        show({
          ...askedFor.current,
          [group]: (askedFor.current[group] ?? 0) + WAITING_WORK_PAGE_LIMIT,
        })
      }
      onShowFewer={(group) => {
        const { [group]: _closed, ...rest } = askedFor.current;
        return show(rest);
      }}
      switchProblem={switchProblem}
      onSwitchTo={async (organisationId) => {
        setSwitchProblem(null);
        const outcome = await switchSessionContext({
          organisationId,
          kind: "case-work",
        });

        if (outcome !== "accepted") {
          setSwitchProblem(outcome);
          return;
        }

        // `useListWaitingWork` lists again when the acting organisation changes.
        closeEveryGroup();
      }}
    />
  );
};

const hintOf = ({
  slice,
  t,
}: {
  slice: WaitingWorkSlice;
  t: TFunction;
}): string | null =>
  match(slice)
    .with({ group: "assessments_in_progress" }, ({ returnedCount, total }) =>
      returnedCount > 0
        ? t("yourActions.groups.assessments_in_progress.hintReturned", {
            count: returnedCount,
          })
        : t("yourActions.groups.assessments_in_progress.hintInProgress", {
            count: total,
          }),
    )
    .with({ group: "assessments_for_review" }, ({ resubmittedCount }) =>
      resubmittedCount > 0
        ? t("yourActions.groups.assessments_for_review.hintResubmitted", {
            count: resubmittedCount,
          })
        : null,
    )
    .with(
      { group: "new_assessments" },
      { group: "unassigned_persons" },
      () => null,
    )
    .exhaustive();

/**
 * The page's rendering, with what is waiting and every action injected so a
 * story or a test can show any state without an API client.
 */
export const YourActionsPageContent = ({
  listing,
  expanded,
  progressOf,
  now,
  onShowAll,
  onShowMore,
  onShowFewer,
  switchProblem,
  onSwitchTo,
}: {
  listing: WaitingWorkListing;
  expanded: Expanded;
  progressOf: (assessmentId: AssessmentId) => ProgressReading | undefined;
  now: Date;
  onShowAll: (group: WaitingWorkGroup) => Promisable<void>;
  onShowMore: (group: WaitingWorkGroup) => Promisable<void>;
  onShowFewer: (group: WaitingWorkGroup) => Promisable<void>;
  switchProblem: SessionContextSwitchProblem | null;
  onSwitchTo: (organisationId: OrganisationId) => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const waiting = waitingShownBy(listing);
  const withRows = (waiting?.groups ?? []).filter((slice) => slice.total > 0);
  const { showsFailure, showsProgress } = match(listing)
    .with({ kind: "failed" }, { kind: "stale" }, () => ({
      showsFailure: true,
      showsProgress: false,
    }))
    .with({ kind: "listing" }, () => ({
      showsFailure: false,
      showsProgress: true,
    }))
    .with({ kind: "listed" }, { kind: "unlisted" }, () => ({
      showsFailure: false,
      showsProgress: false,
    }))
    .exhaustive();

  return (
    <Stack spacing={2.25} sx={{ maxWidth: 1100 }}>
      <Typography variant="h4" component="h1">
        {t("yourActions.title")}
      </Typography>

      {showsFailure ? (
        <Alert severity="error">{t("yourActions.failed")}</Alert>
      ) : null}

      {switchProblem === null ? null : (
        <Alert severity="error">
          {match(switchProblem)
            .with("refused", () => t("errors.sessionContextFailed"))
            .with("failed", () => t("errors.sessionContextUnavailable"))
            .exhaustive()}
        </Alert>
      )}

      {showsProgress ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {waiting?.elsewhere.map((other) => (
        <Alert
          key={other.organisationId}
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void onSwitchTo(other.organisationId)}
            >
              {t("yourActions.switchTo", {
                organisationName: other.organisationName,
              })}
            </Button>
          }
        >
          {t("yourActions.elsewhere", {
            count: other.total,
            organisationName: other.organisationName,
          })}
        </Alert>
      ))}

      {waiting !== undefined && withRows.length === 0 ? (
        <>
          <Paper variant="outlined" sx={{ p: 3.5 }}>
            <Stack spacing={1.25}>
              <Typography variant="h5" component="h2">
                {t("yourActions.clear.title")}
              </Typography>
              <Typography variant="body2">
                {t("yourActions.clear.description")}
              </Typography>
              <Link
                component={RouterLink}
                to={createRoute.persons()}
                sx={{ fontWeight: 600, alignSelf: "flex-start" }}
              >
                {t("yourActions.clear.seeYoungPeople")}
              </Link>
            </Stack>
          </Paper>
          <Stack component="ul" spacing={0.75} sx={{ p: 0, m: 0 }}>
            {waiting.groups.map((slice) => (
              <Stack
                key={slice.group}
                component="li"
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  listStyle: "none",
                  px: 2,
                  py: 1.5,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                }}
              >
                <CheckIcon fontSize="small" color="success" aria-hidden />
                <Typography variant="body2">
                  {t(`yourActions.groups.${slice.group}.clear`)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </>
      ) : null}

      {withRows.length > 0 ? (
        <Box
          component="nav"
          aria-label={t("yourActions.title")}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: `repeat(${String(Math.min(withRows.length, 4))}, minmax(0, 1fr))`,
            },
            gap: 1.75,
          }}
        >
          {withRows.map((slice) => (
            <Link
              key={slice.group}
              href={`#${slice.group}`}
              underline="none"
              color="inherit"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                px: 2.25,
                py: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t(`yourActions.groups.${slice.group}.title`)}
              </Typography>
              <Typography
                sx={{ fontSize: 34, fontWeight: 600, lineHeight: 1.1 }}
                color="primary"
              >
                {slice.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {hintOf({ slice, t }) ?? " "}
              </Typography>
            </Link>
          ))}
        </Box>
      ) : null}

      {withRows.map((slice) => (
        <WaitingWorkGroupSection
          key={slice.group}
          first={slice}
          expanded={expanded[slice.group]}
          progressOf={progressOf}
          now={now}
          onShowAll={() => onShowAll(slice.group)}
          onShowMore={() => onShowMore(slice.group)}
          onShowFewer={() => onShowFewer(slice.group)}
        />
      ))}
    </Stack>
  );
};
